/** biome-ignore-all lint/correctness/noUnusedImports: this is a really stupid lint thing to deal with */
import { CopyText, Tab, TabItem } from '@awell-health/design-system'
import Markdown from 'markdown-to-jsx'
import React, { useState, type FC } from 'react'
import { ActivityAction, ActivityResolution, OrchestratedAgent } from '../../../../generated/types-orchestration'

import {
  AgentDoneBadge,
  AgentFailedBadge
} from '../../../PathwayActivityFeed/components/FeedItems/ActivityCard/AgentCardDetails'
import ActivityDetailsTools from '../components/ActivityDetailsTools'
import { Activity } from '../../types'
import { ExpandableContent } from '../../ExpandableContent'

interface Props {
  activity: Activity
  orchestrationAgent: OrchestratedAgent | null
  agentConfig: any | null
}

export const AgentDetailTab: FC<Props> = ({ agentConfig, orchestrationAgent, activity }) => {
  const [activeTab, setActiveTab] = useState('general')

  if (!agentConfig || !orchestrationAgent) {
    return <div>Agent not found</div>
  }

  const markdownOptions = {
    overrides: {
      p: { props: { className: 'mb-4 last:mb-0' } },
      ol: { props: { className: 'list-decimal ml-6 mb-4 space-y-2' } },
      ul: { props: { className: 'list-disc ml-6 mb-4 space-y-2' } },
      li: { props: { className: 'pl-1' } },
      strong: { props: { className: 'font-semibold' } },
      em: { props: { className: 'italic' } },
      h1: { props: { className: 'text-2xl font-bold' } },
      h2: { props: { className: 'text-xl font-bold' } },
      h3: { props: { className: 'text-lg font-bold' } },
      h4: { props: { className: 'text-base font-bold' } }
    }
  }

  const { name, version, jobToBeDone, stakeholders, tools, instructions } = agentConfig

  if (activity.action !== ActivityAction.Activate) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <div className="text-slate-900 text-xl">Summary</div>
          {activity.resolution === ActivityResolution.Success && <AgentDoneBadge />}
          {activity.resolution === ActivityResolution.Failure && <AgentFailedBadge />}
        </div>
        <div>{orchestrationAgent.execution?.outputs?.summary}</div>
      </div>
    )
  }

  const tabs: TabItem[] = [
    {
      id: 'general',
      label: 'General',
      onClick: () => setActiveTab('general')
    },
    {
      id: 'tools',
      label: 'Tools',
      onClick: () => setActiveTab('tools')
    }
  ]

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="text-lg font-medium text-gray-900">Agent information</div>
      <Tab variant="default" items={tabs} selected={activeTab} fullWidth={false} size="sm" />
      {activeTab === 'general' && (
        <div className="flex flex-col gap-2">
          <div className="text-base text-slate-500 pb-2 pl-2">General information about the setup of your agent.</div>

          <div key="agent_and_version" className="rounded-xl border p-3">
            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium text-gray-900">Agent and version</div>
              <div className="text-sm text-gray-800 bg-slate-50 p-2 border border-slate-200">
                <div>
                  {name} {`(version ${version})`}
                </div>
              </div>
            </div>
          </div>

          <div key="stakeholders" className="rounded-xl border p-3">
            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium text-gray-900">Stakeholders</div>
              <div className="text-sm text-gray-800 bg-slate-50 p-2 border border-slate-200">
                <div>{stakeholders?.map(({ name }: { name: string }) => name).join(', ')}</div>
              </div>
            </div>
          </div>

          <div key="run_id" className="rounded-xl border p-3">
            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium text-gray-900">Run ID</div>
              <div className="text-sm text-gray-800 bg-slate-50 p-2 border border-slate-200">
                <div>
                  <CopyText text={orchestrationAgent.agent_thread_id ?? ''} position="bottom">
                    <code className="text-slate-700 text-base">{orchestrationAgent.agent_thread_id ?? '-'}</code>
                  </CopyText>
                </div>
              </div>
            </div>
          </div>

          {jobToBeDone && (
            <div key="job_to_be_done" className="rounded-xl border p-3">
              <div className="flex flex-col gap-2">
                <div className="text-sm font-medium text-gray-900">Job to be done</div>
                <ExpandableContent content={jobToBeDone ?? ''} />
              </div>
            </div>
          )}

          {instructions && (
            <div key="instructions" className="rounded-xl border p-3">
              <div className="flex flex-col gap-2">
                <div className="text-sm font-medium text-gray-900">Instructions</div>
                <div className="bg-slate-50 p-2 border border-slate-200 ">
                  <div className="prose text-sm prose-p:my-0">
                    <Markdown options={markdownOptions}>{instructions?.replaceAll('\\n', '\n') ?? ''}</Markdown>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {activeTab === 'tools' && (
        <div className="flex flex-col gap-2">
          <div className="text-base text-slate-500 pb-2 pl-2">An overview of the tools available to your agent.</div>
          <ActivityDetailsTools
            tools={tools?.map(({ name, description, type }: { name: string; description: string; type: string }) => ({
              name,
              description,
              type: type as string
            }))}
          />
        </div>
      )}
    </div>
  )
}
